import React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { Popover, TableProps, TableColumnType, Button } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { FlexBoxCenter } from 'styles';

import { TextUnderline } from './styles';

const PopoverFlexBoxCenter = styled(FlexBoxCenter)`
  .anticon-info-circle {
    color: #4dabf7;
    cursor: pointer;
  }
`;
export const PopoverHeaderCell = ({ title, popoverContent }) => (
  <PopoverFlexBoxCenter justify="center" gap="5px">
    <span>{title}</span>
    <Popover content={popoverContent} overlayInnerStyle={{ whiteSpace: 'break-spaces' }}>
      <InfoCircleOutlined />
    </Popover>
  </PopoverFlexBoxCenter>
);

export interface TSettlementDetail {
  settlementIdx: number;
  settlementName: string;
  status: string;
  evidenceStatus: string;
  serviceTransactionAmount: number;
  feeAmount: number;
  partnerContractIdx: number;
  pointDirectAmount: number;
  settlementAmount: number;
  settlementStartDate: string;
  settlementEndDate: string;
  isEvidenceMerge: true;
  isPayCommissionMerge: true;
  taxableEvidenceType: string;
  feeCalcType: 'PER' | 'CLIENT_SUM' | 'PARTNER_SUM';
  feeCalcTypeName: string;
  evidenceDeadlineDate: string;
  payDate: string;
  bizAccountIdx: number;
  bizName: string;
  bizNumber: string;
  bizSubNumber: string;
  bizPresidentName: string;
  bizAddress: string;
  bizCategory: string;
  bizItem: string;
  accountBizEmail: string;
  certificateExpiredDate: string;
}

const defaultColumnOptions: TableColumnType<TSettlementDetail> = { align: 'center', render: (value) => value || '-' };
const numberColumnOption: TableColumnType<TSettlementDetail> = {
  align: 'right',
  render: (value) => (typeof value === 'number' ? Number(value).toLocaleString() : '-')
};

export const settlementAmountColumns: TableProps<TSettlementDetail>['columns'] = [
  {
    title: '매출금액(A)',
    dataIndex: 'serviceTransactionAmount',
    width: '25%',
    align: 'center',
    render: (value) => (typeof value === 'number' ? Number(value).toLocaleString() : '-')
  },
  {
    title: '수수료(B)',
    dataIndex: 'feeAmount',
    width: '25%',
    render: (value) => (
      <div style={{ textAlign: 'center', color: '#F44336' }}>
        {typeof value === 'number' ? Number(value).toLocaleString() : '-'}
      </div>
    )
  },
  {
    title: '직접정산(C)',
    dataIndex: 'pointDirectAmount',
    width: '25%',
    render: (value) => (
      <div style={{ textAlign: 'center', color: '#F44336' }}>
        {typeof value === 'number' ? Number(value).toLocaleString() : '-'}
      </div>
    )
  },
  {
    title: <div style={{ color: '#46B476' }}>총 정산금액(A-B-C)</div>,
    dataIndex: 'settlementAmount',
    width: '25%',
    align: 'center',
    render: (value) => (typeof value === 'number' ? Number(value).toLocaleString() : '-')
  }
];

export const settlementBizColumns: TableProps<any>['columns'] = [
  { title: '상호명', dataIndex: 'bizName', ...defaultColumnOptions },
  { title: '사업자번호', dataIndex: 'bizNumber', ...defaultColumnOptions },
  { title: '종사업장번호', dataIndex: 'bizSubNumber', ...defaultColumnOptions },
  { title: '대표자명', dataIndex: 'bizPresidentName', ...defaultColumnOptions },
  { title: '주소', dataIndex: 'bizAddress', ...defaultColumnOptions },
  { title: '업태', dataIndex: 'bizCategory', ...defaultColumnOptions },
  { title: '종목', dataIndex: 'bizItem', ...defaultColumnOptions }
];

//
//
//
// 매출 정산 상세 / 매출 내역 탭 / 거래 건 별
export const perSalesHistoryColumns: TableProps<any>['columns'] = [
  {
    title: '거래일시',
    dataIndex: 'paymentDate',
    align: 'center',
    width: '220px',
    render: (value) => {
      return value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-';
    }
  },
  { title: '제휴점명', dataIndex: 'storeName', ...defaultColumnOptions },
  { title: '회사명', dataIndex: 'companyName', ...defaultColumnOptions },
  { title: '사용자명', dataIndex: 'userName', ...defaultColumnOptions },
  { title: '식대포인트', dataIndex: 'pointAmount', ...numberColumnOption },
  { title: '대장페이', dataIndex: 'captainPayAmount', ...numberColumnOption },
  { title: '배송비', dataIndex: 'deliveryAmount', ...numberColumnOption },
  {
    title: '결제금액',
    align: 'right',
    render: (_, values) => {
      const number = (values?.relayedPointAmount || 0) + (values?.captainPayAmount || 0) + (values?.deliveryAmount || 0);
      return typeof number === 'number' ? Number(number).toLocaleString() : '-';
    }
  },
  {
    title: '정산(예정)금액',
    dataIndex: 'settlementAmount',
    align: 'right',
    render: (value) => {
      return typeof value === 'number' ? Number(value).toLocaleString() : '-';
    }
  },
  { title: '상태', dataIndex: 'statusName', ...defaultColumnOptions }
];

// 매출 정산 상세 / 매출 내역 탭 / 회사 별
export const clientSalesHistoryColumns: TableProps<any>['columns'] = [
  { title: '제휴점명', dataIndex: 'storeName', ...defaultColumnOptions },
  { title: '회사명', dataIndex: 'companyName', ...defaultColumnOptions },
  { title: '식대포인트', dataIndex: 'pointAmount', ...numberColumnOption },
  { title: '대장페이', dataIndex: 'captainPayAmount', ...numberColumnOption },
  { title: '배송비', dataIndex: 'deliveryAmount', ...numberColumnOption },
  { title: '식사 수', dataIndex: 'mealCount', ...numberColumnOption },
  { title: '취소 수', dataIndex: 'cancelCount', ...numberColumnOption },
  { title: '정산방식', dataIndex: 'transactionStructureName', ...defaultColumnOptions },
  { title: '수수료 계산방식', dataIndex: 'paymentPolicyName', ...defaultColumnOptions },
  {
    title: '포인트 수수료율',
    dataIndex: 'pointCommissionRate',
    align: 'right',
    render: (value) => {
      return typeof value === 'number' ? `${value}%` : '-';
    }
  },
  {
    title: '대장페이 수수료율',
    dataIndex: 'captainPayCommissionRate',
    align: 'right',
    render: (value) => {
      return typeof value === 'number' ? `${value}%` : '-';
    }
  },
  {
    title: '총 결제금액',
    align: 'right',
    render: (_, values) => {
      const number = (values.relayedPointAmount || 0) + (values.captainPayAmount || 0) + (values.deliveryAmount || 0);
      return typeof number === 'number' ? Number(number).toLocaleString() : '-';
    }
  },
  {
    title: '정산(예정)금액',
    dataIndex: 'settlementAmount',
    align: 'right',
    render: (value) => {
      return typeof value === 'number' ? Number(value).toLocaleString() : '-';
    }
  }
];

// 매출 정산 상세 / 매출 내역 탭 / 제휴점 별
export const partnerSalesHistoryColumns: TableProps<any>['columns'] = [
  { title: '제휴점명', dataIndex: 'storeName', ...defaultColumnOptions },
  { title: '식대포인트', dataIndex: 'pointAmount', ...numberColumnOption },
  { title: '대장페이', dataIndex: 'captainPayAmount', ...numberColumnOption },
  { title: '배송비', dataIndex: 'deliveryAmount', ...numberColumnOption },
  { title: '식사 수', dataIndex: 'mealCount', ...numberColumnOption },
  { title: '취소 수', dataIndex: 'cancelCount', ...numberColumnOption },
  {
    title: '포인트 수수료율',
    dataIndex: 'pointCommissionRate',
    align: 'right',
    render: (value) => {
      return typeof value === 'number' ? `${value}%` : '-';
    }
  },
  {
    title: '대장페이 수수료율',
    dataIndex: 'captainPayCommissionRate',
    align: 'right',
    render: (value) => {
      return typeof value === 'number' ? `${value}%` : '-';
    }
  },
  {
    title: '총 결제금액',
    align: 'right',
    render: (_, values) => {
      const number = (values?.relayedPointAmount || 0) + (values?.captainPayAmount || 0) + (values?.deliveryAmount || 0);
      return typeof number === 'number' ? Number(number).toLocaleString() : '-';
    }
  },
  {
    title: '정산 (예정)금액',
    dataIndex: 'settlementAmount',
    align: 'right',
    render: (value) => {
      return typeof value === 'number' ? Number(value).toLocaleString() : '-';
    }
  }
];

const carryOverHistoryColumns: TableProps<any>['columns'] = [
  {
    title: '거래일시',
    dataIndex: 'paymentDate',
    align: 'center',
    width: '220px',
    render: (value) => {
      return value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-';
    }
  },
  {
    title: '생성일시',
    dataIndex: 'paymentCreateDate',
    align: 'center',
    width: '220px',
    render: (value) => {
      return value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-';
    }
  },
  { title: '제휴점명', dataIndex: 'storeName', ...defaultColumnOptions },
  { title: '회사명', dataIndex: 'companyName', ...defaultColumnOptions },
  { title: '사용자명', dataIndex: 'userName', ...defaultColumnOptions },
  { title: '식대포인트', dataIndex: 'pointAmount', ...numberColumnOption },
  { title: '대장페이', dataIndex: 'captainPayAmount', ...numberColumnOption },
  { title: '배송비', dataIndex: 'deliveryAmount', ...numberColumnOption },
  {
    title: '결제금액',
    align: 'right',
    render: (_, values) => {
      const number = (values?.relayedPointAmount || 0) + (values?.captainPayAmount || 0) + (values?.deliveryAmount || 0);
      return typeof number === 'number' ? Number(number).toLocaleString() : '-';
    }
  },
  {
    title: '정산(예정)금액',
    dataIndex: 'settlementAmount',
    align: 'right',
    render: (value) => {
      return typeof value === 'number' ? Number(value).toLocaleString() : '-';
    }
  },
  { title: '상태', dataIndex: 'statusName', ...defaultColumnOptions }
];

export const getSalesHistoryColumnsMap = (feeCalcType?: 'PER_SALES' | 'CLIENT_SALES' | 'PARTNER_SALES' | 'CARRY_OVER') => {
  const columsMap = {
    PER_SALES: perSalesHistoryColumns,
    CLIENT_SALES: clientSalesHistoryColumns,
    PARTNER_SALES: partnerSalesHistoryColumns,
    CARRY_OVER: carryOverHistoryColumns
  };
  return columsMap[feeCalcType];
};

// 정산서 증빙 내역 조회
export const evidencesColumns = ({ onCellClick }): TableProps<any>['columns'] => [
  { title: '증빙유형', dataIndex: 'evidenceTypeName', width: '10%', ...defaultColumnOptions },
  { title: '품목', dataIndex: 'itemName', width: '8%', ...defaultColumnOptions },
  { title: '공급하는자', dataIndex: 'providerBizName', width: '7%', ...defaultColumnOptions },
  { title: '대표자명', dataIndex: 'providerBizPresidentName', width: '7%', ...defaultColumnOptions },
  { title: '사업자번호', dataIndex: 'providerBizNumber', width: '10%', ...defaultColumnOptions },
  { title: '작성일자', dataIndex: 'writeDate', width: '8%', ...defaultColumnOptions },
  { title: '공급가액', dataIndex: 'supplyAmount', width: '8%', ...numberColumnOption },
  { title: '부가세액', dataIndex: 'vatAmount', width: '8%', ...numberColumnOption },
  { title: '총액', dataIndex: 'totalAmount', width: '8%', ...numberColumnOption },
  { title: '승인번호', dataIndex: 'confirmNumber', width: '10%', ...defaultColumnOptions },
  { title: '승인일자', dataIndex: 'sendDate', width: '8%', ...defaultColumnOptions },
  {
    title: '상태',
    dataIndex: 'evidenceStatusName',
    width: '8%',
    align: 'center',
    render: (value, values) => {
      return <TextUnderline onClick={() => onCellClick(values)}>{value}</TextUnderline>;
    }
  }
];

// 정산서 증빙 내역 조회 - 첨부파일
export const attachmentsColumns: TableProps<any>['columns'] = [
  { title: '첨부 자료명', dataIndex: 'fileName', align: 'center', width: '90%' },
  {
    title: ' ',
    align: 'center',
    width: '10%',
    render: (_, values) => {
      return (
        <Button
          style={{
            height: '30px',
            padding: '0 18px',
            background: '#212529',
            borderRadius: '4px',
            color: '#ffffff',
            fontSize: '14px',
            lineHeight: '24px'
          }}
          onClick={() => {
            if (!values?.fileName || !values?.fileUrl) return;
            fetch(values.fileUrl, { method: 'GET' })
              .then((res) => res.blob())
              .then((blob) => {
                const blobURL = URL.createObjectURL(blob);

                const element = document.createElement('a');
                element.href = blobURL;
                element.download = values.fileName;
                element.click();
                element.remove();
              });
          }}
        >
          다운로드
        </Button>
      );
    }
  }
];

// 입금/이체 내역 - 입금 내역
export const depositColumns: TableProps<any>['columns'] = [
  { title: '예금주명', dataIndex: 'accountHolder', align: 'center', width: '25%' },
  { title: '은행명', dataIndex: 'bankName', align: 'center', width: '25%' },
  { title: '계좌번호', dataIndex: 'accountNumber', align: 'center', width: '25%' },
  { title: '입금 받을 금액', dataIndex: 'depositAmount', width: '25%', ...numberColumnOption }
];

export const depositItemColumns: TableProps<any>['columns'] = [
  {
    title: '거래 일시',
    dataIndex: 'transactionDate',
    align: 'center',
    width: '240px',
    render: (value) => (value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-')
  },
  { title: '입금액(원)', dataIndex: 'amount', width: '180px', ...numberColumnOption },
  { title: '적요', dataIndex: 'description', align: 'left' }
];

// 입금/이체 내역 - 이체 확인
export const transferColumns: TableProps<any>['columns'] = [
  { title: '예금주명', dataIndex: 'accountHolder', align: 'center', width: '25%' },
  { title: '은행명', dataIndex: 'bankName', align: 'center', width: '25%' },
  { title: '계좌번호', dataIndex: 'accountNumber', align: 'center', width: '25%' },
  { title: '이체 필요 금액', dataIndex: 'transferAmount', width: '25%', ...numberColumnOption }
];

export interface TEvidenceDetail {
  evidenceIdx: number;
  evidenceType: string;
  evidenceTypeName: string;
  transactionType: string;
  transactionTypeName: string;
  confirmNumber: string;
  providerBizNumber: string;
  providerBizSubNumber: string;
  providerBizName: string;
  providerBizPresidentName: string;
  providerAddress: string;
  providerBizCategory: string;
  providerBizItem: string;
  providerEmail: string;
  receiverBizNumber: string;
  receiverBizSubNumber: string;
  receiverBizName: string;
  receiverBizPresidentName: string;
  receiverAddress: string;
  receiverBizCategory: string;
  receiverBizItem: string;
  receiverEmail1: string;
  receiverEmail2: string;
  writeDate: string;
  totalAmount: number;
  supplyAmount: number;
  vatAmount: number;
  correctionReason: string;
  cashAmount: number;
  checkAmount: number;
  noteAmount: number;
  creditAmount: number;
  evidenceItems: {
    writeDate: string;
    itemName: string;
    quantity: number;
    unitPrice: number;
    supplyAmount: number;
    vatAmount: number;
    totalAmount: number;
  }[];
}

// 증빙 현금영수증 상세
export interface TEvidenceCashReceiptDetail {
  evidenceIdx: number;
  purposeType: string;
  purposeTypeName: string;
  transactionType: string;
  transactionTypeName: string;
  issuedByNumber: string;
  writeDate: string;
  confirmNumber: string;
  totalAmount: number;
  supplyAmount: number;
  vatAmount: number;
  merchantName: string;
  merchantBizNumber: string;
}

export const certificateStatusMaps = {
  PAPER_TAX_INVOICE: { content: `세금계산서 발급 요청을 진행 하시겠습니까?`, okText: '승인하기' },
  CERTIFICATE: { content: `세금계산서 발급 요청을 진행 하시겠습니까?`, okText: '발급하기' },
  NOT_CERTIFICATE: {
    content: `세금계산서 발급 전에 전자세금용 인증서 등록을\n발급 대행업체에서 진행합니다.`,
    okText: '등록하기'
  },
  EXPIRED_CERTIFICATE: {
    content: `기존 등록한 인증서가 만료되어\n전자세금용 인증서 등록을 진행해야 합니다.`,
    okText: '등록하기'
  }
};
